/** «Compartir y acceso»: quién ve y quién edita una página, y su enlace público. */
import { useState } from 'react'

import { ErrorDeApi, api } from '../api'
import { Dialogo } from '../componentes/piezas'
import { usePortal } from '../contexto'
import type { Nivel, Pagina } from './tipos'

const NIVELES: { valor: Nivel; texto: string }[] = [
  { valor: 'member', texto: 'Todo el portal' },
  { valor: 'staff', texto: 'Staff y superiores' },
  { valor: 'developer', texto: 'Desarrollo y administración' },
  { valor: 'admin', texto: 'Sólo administración' },
]
const NOMBRE = Object.fromEntries(NIVELES.map((n) => [n.valor, n.texto])) as Record<Nivel, string>

export default function Acceso({ pagina, rutaPaginas, alCambiar, alCerrar }: {
  pagina: Pagina
  rutaPaginas: string
  alCambiar: (p: Pagina) => void
  alCerrar: () => void
}) {
  const { avisar } = usePortal()
  const [ocupado, setOcupado] = useState(false)
  const [copiado, setCopiado] = useState(false)
  const gestiona = pagina.puede_gestionar_acceso
  const enlace = pagina.enlace ? `${window.location.origin}/p/${pagina.enlace}` : null

  const llamar = async (accion: () => Promise<Pagina>, fallo: string) => {
    setOcupado(true)
    try {
      alCambiar(await accion())
    } catch (e) {
      avisar(e instanceof ErrorDeApi ? e.message : fallo, 'malo')
    } finally {
      setOcupado(false)
    }
  }

  const poner = (campo: 'view_level' | 'edit_level', valor: string) =>
    llamar(() => api.patch<Pagina>(`${rutaPaginas}/${pagina.id}`, { [campo]: valor || null }),
           'No se ha podido cambiar el acceso.')

  const publicar = (valor: boolean) =>
    llamar(() => api.put<Pagina>(`${rutaPaginas}/${pagina.id}/publica`, { valor }),
           'No se ha podido cambiar la publicación.')

  const copiar = async () => {
    if (!enlace) return
    try {
      await navigator.clipboard.writeText(enlace)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2000)
    } catch {
      avisar('No se ha podido copiar. Selecciónalo y cópialo a mano.', 'malo')
    }
  }

  return (
    <Dialogo titulo="Compartir y acceso" alCerrar={alCerrar}>
      <section className="acceso">
        <p className="acceso__resumen">
          Pueden verla: <strong>{NOMBRE[pagina.efectivo_ver].toLowerCase()}</strong>. Pueden editarla:{' '}
          <strong>{NOMBRE[pagina.efectivo_editar].toLowerCase()}</strong>.
        </p>

        <div className="campo">
          <label htmlFor="ver">Quién puede verla</label>
          <select id="ver" value={pagina.nivel_ver ?? ''} disabled={!gestiona || ocupado}
                  onChange={(e) => void poner('view_level', e.target.value)}>
            <option value="">Lo que diga la página de arriba</option>
            {NIVELES.map((n) => <option key={n.valor} value={n.valor}>{n.texto}</option>)}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="editar">Quién puede editarla</label>
          <select id="editar" value={pagina.nivel_editar ?? ''} disabled={!gestiona || ocupado}
                  onChange={(e) => void poner('edit_level', e.target.value)}>
            <option value="">Lo que diga la página de arriba (por defecto, staff)</option>
            {NIVELES.map((n) => <option key={n.valor} value={n.valor}>{n.texto}</option>)}
          </select>
          <div className="campo__ayuda">
            Una página nunca es más abierta que aquello donde cuelga: si la de arriba está
            restringida, ésta también, y todo lo que haya dentro.
            {!gestiona && ' Sólo desarrollo y administración pueden cambiar esto.'}
          </div>
        </div>

        {pagina.kind === 'page' && (pagina.puede_publicar || pagina.publica) && (
          <div className="acceso__publica">
            <h3>Publicar en la web</h3>
            {pagina.publica ? (
              <>
                <p>
                  Cualquiera con este enlace puede <strong>leer</strong> esta página, sin iniciar
                  sesión. No ve sus subpáginas, comentarios ni historial.
                </p>
                {enlace ? (
                  <div className="acceso__enlace">
                    <input readOnly value={enlace} onFocus={(e) => e.target.select()}
                           aria-label="Enlace público" />
                    <button className="boton" onClick={() => void copiar()}>
                      {copiado ? 'Copiado' : 'Copiar'}
                    </button>
                  </div>
                ) : (
                  <p className="campo__ayuda">El enlace sólo lo ven quienes pueden editar la página.</p>
                )}
                {pagina.puede_publicar && (
                  <button className="boton boton--malo" disabled={ocupado}
                          onClick={() => void publicar(false)}>
                    Dejar de publicar
                  </button>
                )}
              </>
            ) : (
              <>
                <p>
                  Crea un enlace que abre esta página <strong>a cualquier persona de internet</strong>,
                  sin iniciar sesión. Sólo se podrá leer. Piensa si el contenido es para todo el mundo.
                </p>
                <button className="boton boton--principal" disabled={ocupado}
                        onClick={() => void publicar(true)}>
                  Publicar esta página
                </button>
              </>
            )}
          </div>
        )}
      </section>
    </Dialogo>
  )
}
